import { appBus } from '@glu/core';
import Gateway from 'system/gateway';
import Model from './models/baseNestedModel';
import urls from './urls';
import integrationUtils from './integrationUtils';

class CurrentUser extends Model {
  // eslint-disable-next-line class-methods-use-this
  defaults() {
    if (!integrationUtils.isPMX()) {
      return {};
    }

    // paymode has a single PTX connect user and does not set these flags so needs them as defaults
    return {
      isSender: true,
      isNewAccount: false,
      isAdmin: true,
      isBtsUser: false,
      isBrand: false,
      individual: false,
      companyName: '',
      documentRetentionEnabled: false
    };
  }

  initialize() {
    this.listenTo(
      appBus,
      'do:logout',
      () => {
        this.logout(this.trigger.bind(this, 'logout:done'));
      }
    );
  }

  // eslint-disable-next-line class-methods-use-this
  urlRoot() {
    return urls.account;
  }

  isBrand() {
    return this.get('isBrand');
  }

  isAdmin() {
    return this.get('isAdmin') && this.isSender();
  }

  isNewSender() {
    return this.get('isSender') && this.get('isNewAccount');
  }

  isSender() {
    return this.get('isSender');
  }

  // eslint-disable-next-line class-methods-use-this
  isBtsUser() {
    return true; // this.get('isBtsUser');  //ToDo when Admin code is available
  }

  hasLogos() {
    return this.get('numberOfLogos');
  }

  hasProjects() {
    return this.get('numberOfProjects');
  }

  hasProfiles() {
    return this.get('numberOfMailingProfiles');
  }

  hasChequeProfiles() {
    return this.get('hasChequeProfiles');
  }

  hasBTSProfiles() {
    return this.isAdmin() || this.get('hasBTSProfiles');
  }

  hasDocumentProfiles() {
    return this.get('hasDocumentProfiles');
  }

  hasBtsEnabled() {
    return this.get('btsEnabled');
  }

  installationCompleted() {
    return this.hasProjects() && this.hasLogos() && this.hasProfiles();
  }

  // eslint-disable-next-line class-methods-use-this
  getUiVersion(callback) {
    return new Gateway(urls.uiVersion.toString())
      .get()
      .then(() => { if (callback) callback(); })
      .catch(() => { if (callback) callback(); });
  }

  isRegistered() {
    return this.has('userId');
  }

  getUserName() {
    if (this.isIndividual()) {
      return this.getFullName();
    }

    return this.getCompanyName();
  }

  isIndividual() {
    return this.get('individual');
  }

  getFullName() {
    const fullName = String(`${this.getFirstName()} ${this.getLastName()}`).trim();

    return !fullName.length ? this.getEmail() : fullName;
  }

  getCompanyName() {
    return this.get('companyName');
  }

  getLastName() {
    return this.get('lastName') || '';
  }

  getFirstName() {
    return this.get('firstName') || '';
  }

  getEmail() {
    return this.get('username');
  }

  compareEmail(email) {
    return this.getEmail() === email;
  }

  compareIds(id) {
    return this.getUserId() === id;
  }

  compareAccounts(id) {
    return this.get('accountId') === id;
  }

  getUserId() {
    return this.get('currentUser.id');
  }

  getConnectUserId() {
    return this.get('userId');
  }

  hasDocumentRetention() {
    return this.get('documentRetentionEnabled');
  }

  getProfilePermissions() {
    return this.get('profilePermissions');
  }

  getBTSProfilePermissions() {
    return this.get('btsProfilePermissions');
  }

  hasAtLeastOnePermission(permission, otherConditions, permissions = this.getProfilePermissions()) {
    return (!permissions
      || permissions.filter((perm) => {
        if (typeof otherConditions === 'function' && !otherConditions(perm)) {
          return false;
        }
        return perm[permission];
      }).length
    );
  }

  hasAtLeastOneBTSCreatePermission() {
    return this.hasAtLeastOnePermission('canCreate', null, this.getBTSProfilePermissions());
  }

  hasAtLeastOneChequeCreatePermission() {
    return this.hasAtLeastOnePermission('canCreate', perm => perm.documentType === 'Cheque');
  }

  hasAtLeastOneDocumentCreatePermission() {
    return this.hasAtLeastOnePermission('canView', perm => perm.documentType !== 'Cheque');
  }

  hasAtLeastOneChequeApprovePermission() {
    return this.hasAtLeastOnePermission('canApprove', perm => perm.documentType === 'Cheque');
  }

  hasPermission(permissionName, id) {
    const permissions = this.getProfilePermissions();
    if (!permissions) {
      /**
             * An admin has profilePermissions set to null, hence returning true here
             */
      return true;
    }

    const profilePermission = permissions.filter(permission => permission.documentProfileId === id);
    if (!profilePermission.length) {
      return false;
    }

    return !!profilePermission[0][permissionName];
  }

  isPromptToPayEnabled() {
    return this.get('promptToPayEnabled');
  }

  isDirectDebitEnabled() {
    return this.get('directDebitEnabled');
  }

  isPayDirectEnabled() {
    return this.get('payDirectEnabled');
  }

  isCardsEnabled() {
    return this.get('cardsEnabled');
  }
}

export default new CurrentUser();
